import styled from '@emotion/styled';
import { Stack, Text, tokens, Tooltip, Icon, Loader } from '@hotjar/hotsauce';
import type { Currency } from '@hotjar/product-catalog';
import { useTranslationWithPrefix } from '@hotjar/translations';
import type { ReactNode } from 'react';
import type { DiscountsType } from '~/subscription-management/components/discount/Discounts';
import { Discounts } from '~/subscription-management/components/discount/Discounts';

interface Props {
  loading: boolean;
  children?: ReactNode;
  className?: string;
}

const S = {
  Container: styled.div`
    ${tokens.flexContainer('default', { direction: 'column' })};
    border: 1px solid ${tokens.color.ui_border};
    border-radius: ${tokens.spacer.x0pt5};
    background-color: ${tokens.color.white};
    padding: ${tokens.spacer.x2};
  `,
  Row: styled(Stack)`
    justify-content: space-between;
  `,
  SectionTaxes: styled.section`
    display: flex;
    flex-direction: column;
    gap: ${tokens.spacer.x0pt5};
    margin-bottom: ${tokens.spacer.x1};
  `,
  SectionDiscount: styled.div`
    margin-top: ${tokens.spacer.x1pt5};
  `,
  RowCaption: styled(Stack)`
    justify-content: space-between;
    align-items: center;
  `,
  TextWithTooltip: styled.div`
    display: flex;
    gap: ${tokens.spacer.x0pt5};
    align-items: center;
  `,
};

export function PriceReview({ loading, children, className }: Props): JSX.Element {
  const { t } = useTranslationWithPrefix('checkout:wizard.cost-changes.pay-later');

  return (
    <S.Container
      aria-label={t('aria-label')}
      aria-busy={loading}
      aria-live="polite"
      aria-atomic="true"
      aria-relevant="all"
      className={className}
    >
      <div>{(children !== undefined || children !== false) && <div>{children}</div>}</div>
    </S.Container>
  );
}

interface PriceProps {
  loading: boolean;
  title: string;
  price: string;
}
const Price = ({ title, price, loading }: PriceProps): JSX.Element | null => {
  return (
    <S.Row spacing="x1">
      <Text textType="subtitle1" textColor="text_default">
        {title}
      </Text>
      <Text textType="subtitle1" textColor="text_default">
        <Loader isLoading={loading}>{price}</Loader>
      </Text>
    </S.Row>
  );
};
PriceReview.Price = Price;

interface ExtraInfoProps {
  subtitle?: string;
  subtitleTooltip?: string;
  priceDescription?: ReactNode;
}
const ExtraInfo = ({ subtitle, subtitleTooltip, priceDescription }: ExtraInfoProps) => {
  return (
    <S.Row>
      <Text textType="caption" textColor="text_light">
        <S.TextWithTooltip>
          {subtitleTooltip !== undefined && (
            <Tooltip text={subtitleTooltip}>
              <Icon icon="info" />
            </Tooltip>
          )}
          <span>{subtitle}</span>
        </S.TextWithTooltip>
      </Text>
      <Text textType="subtitle3" textColor="text_default">
        {priceDescription}
      </Text>
    </S.Row>
  );
};
PriceReview.ExtraInfo = ExtraInfo;

interface DiscountProps {
  accountDiscounts?: DiscountsType[];
  currency?: Currency;
  className?: string;
  loading?: boolean;
}
const AccountDiscount = ({
  accountDiscounts,
  currency,
  className,
  loading = false,
}: DiscountProps): JSX.Element | null => {
  if (
    accountDiscounts === undefined ||
    accountDiscounts.length === 0 ||
    currency === undefined ||
    loading
  ) {
    return null;
  }
  return (
    <S.SectionDiscount className={className}>
      <Discounts discounts={accountDiscounts} currency={currency} />
    </S.SectionDiscount>
  );
};
PriceReview.AccountDiscount = AccountDiscount;

interface TaxesProps {
  priceWithoutTax?: string;
  priceTax?: string;
  accountDiscounts?: DiscountsType[];
  currency?: Currency;
  loading?: boolean;
}
const TaxesDiscounts = styled(AccountDiscount)`
  margin-top: 0;
`;
const Taxes = ({
  priceTax,
  priceWithoutTax,
  accountDiscounts,
  currency,
  loading = false,
}: TaxesProps): JSX.Element | null => {
  const { t } = useTranslationWithPrefix('checkout:wizard.cost-changes.pay-later');

  const showTaxes = priceWithoutTax !== undefined && priceTax !== undefined;

  if (!showTaxes) {
    return (
      <S.SectionTaxes>
        <TaxesDiscounts accountDiscounts={accountDiscounts} currency={currency} loading={loading} />
      </S.SectionTaxes>
    );
  }

  return (
    <S.SectionTaxes>
      <S.RowCaption>
        <Text textType="caption" textColor="text_light">
          {t('subtotal-before-tax')}
        </Text>
        {!loading && (
          <Text textType="caption" textColor="text_light">
            {priceWithoutTax}
          </Text>
        )}
      </S.RowCaption>

      <TaxesDiscounts accountDiscounts={accountDiscounts} currency={currency} loading={loading} />

      <S.RowCaption>
        <Text textType="caption" textColor="text_light">
          <S.TextWithTooltip>
            <span>{t('taxes')} </span>
            <Tooltip text={t('taxes-info')}>
              <Icon icon="info" />
            </Tooltip>
          </S.TextWithTooltip>
        </Text>
        {!loading && (
          <Text textType="caption" textColor="text_light">
            {priceTax}
          </Text>
        )}
      </S.RowCaption>
    </S.SectionTaxes>
  );
};
PriceReview.Taxes = Taxes;
